import { useState } from 'react';
import { ExternalLinkIcon, GraduationCapIcon, MailIcon, SendIcon, XIcon } from 'lucide-react';

// Thema's, links en omschrijvingen van hupper.nl/colleges (meta-omschrijving
// per college, opgehaald 3 sep 2026; links uit docs/specs/e-learning/hupper-links.md).
// Hupper levert geen API; de lijst is bewust een constante zodat een wijziging
// een codewijziging is.
export const HUPPER_COLLEGES = [
  {
    thema: 'Autisme',
    url: 'https://hupper.nl/colleges/autisme/',
    beschrijving:
      'Wat autisme is, welke kenmerken erbij horen en hoe ermee om te gaan. Psychiater Bram Sizoo over deze ontwikkelingsstoornis.',
  },
  {
    thema: 'Borderline',
    url: 'https://hupper.nl/colleges/borderline/',
    beschrijving:
      'Wat borderline is, welke kenmerken erbij horen en hoe ermee om te gaan. Psychiater Erwin van Meekeren over de aanpak van deze emotionele rollercoaster.',
  },
  {
    thema: 'Dementie',
    url: 'https://hupper.nl/colleges/dementie/',
    beschrijving:
      'Dementie is meer dan alleen geheugenverlies. Psychiater Martin Kat bespreekt de symptomen en onderliggende oorzaken zoals de ziekte van Alzheimer.',
  },
  {
    thema: 'Dwang',
    url: 'https://hupper.nl/colleges/dwang/',
    beschrijving:
      'Wat er omgaat in mensen met een dwangstoornis. Psychiater Menno Oosterhoff deelt zijn eigen belevingen en die van zijn patiënten.',
  },
  {
    thema: 'Eetstoornissen',
    url: 'https://hupper.nl/colleges/eetstoornissen/',
    beschrijving:
      'Anorexia, boulimia en de eetbuistoornis: wat deze eetstoornissen kenmerkt, hoe ze ontstaan en hoe herstel verloopt.',
  },
  {
    thema: 'Gedragsproblemen bij jongeren',
    url: 'https://hupper.nl/colleges/gedragsproblemen-bij-jongeren/',
    beschrijving:
      'De omgang met jongeren vraagt om flexibiliteit, maar ook om een kader. Wat kan wel en wat kan niet? Over de aanpak van gedragsproblemen.',
  },
  {
    thema: 'Gedragsverandering',
    url: 'https://hupper.nl/colleges/gedragsverandering/',
    beschrijving:
      'Welke factoren gedragsverandering bemoeilijken, wanneer therapie een optie is en de formule voor duurzame gedragsverandering.',
  },
  {
    thema: 'Geluk',
    url: 'https://hupper.nl/colleges/geluk/',
    beschrijving:
      'Wat helpt om het eigen geluk en dat van anderen te bevorderen, aan de hand van de Tafel van geluk van GZ-psycholoog Martin Appelo.',
  },
  {
    thema: 'Hechting en relaties',
    url: 'https://hupper.nl/colleges/hechting-en-relaties/',
    beschrijving:
      'Hoe (on)veilige hechting tot stand komt en hoe relatieproblemen die daaruit voortvloeien op te lossen zijn. Met de wagenmenner-metafoor van GZ-psycholoog Martin Appelo.',
  },
  {
    thema: 'Narcisme',
    url: 'https://hupper.nl/colleges/narcisme/',
    beschrijving:
      'Wat narcisme is, welke kenmerken erbij horen en hoe ermee om te gaan. GZ-psycholoog Martin Appelo bespreekt de impact van deze stoornis, ook in positieve zin.',
  },
  {
    thema: 'Oplossingsgericht werken',
    url: 'https://hupper.nl/colleges/oplossingsgericht-werken/',
    beschrijving:
      'Oplossingsgericht werken is een doelgerichte methode waarin problemen plaatsmaken voor oplossingen. Over de zes sleutels tot succes.',
  },
  {
    thema: 'Positieve psychologie',
    url: 'https://hupper.nl/colleges/positieve-psychologie/',
    beschrijving:
      'Positieve psychologie verlegt de focus van klachten naar krachten. Dr. Hugo Alberts over het herkennen en beter benutten van eigen kwaliteiten.',
  },
  {
    thema: 'Professioneel hulpverlenen',
    url: 'https://hupper.nl/colleges/professioneel-hulpverlenen/',
    beschrijving:
      'Een gespreksmethodiek die toepasbaar is in allerlei soorten hulpverleningsrelaties, met tools om cliënten te begeleiden.',
  },
  {
    thema: 'Suïcide',
    url: 'https://hupper.nl/colleges/suicide/',
    beschrijving:
      'Hoe te herkennen of iemand suïcidaal is, en wat de omgeving kan doen om het risico op zelfmoord te verkleinen.',
  },
  {
    thema: 'Ups & downs in gezinsrelaties',
    url: 'https://hupper.nl/colleges/ups-downs-in-gezinsrelaties/',
    beschrijving:
      'Wat er met de partnerrelatie gebeurt na de komst van kinderen, waardoor een kink in de kabel ontstaat en hoe dat te voorkomen of te herstellen is.',
  },
  {
    thema: 'Zelfcompassie en zelfvertrouwen',
    url: 'https://hupper.nl/colleges/zelfcompassie-en-zelfvertrouwen/',
    beschrijving:
      'Waarom mildheid naar zichzelf betere uitkomsten biedt dan zelfkritiek, en hoe vanuit zelfcompassie om te gaan met problemen die zich aandienen.',
  },
] as const;
export type HupperCollege = (typeof HUPPER_COLLEGES)[number];

/** mailto zonder adres: de gebruiker vult zelf de teamleider in. */
export function hupperMailto({ thema, url }: HupperCollege): string {
  const subject = `Aanvraag Hupper-college: ${thema}`;
  const body = [
    'Hoi,',
    '',
    `Ik kwam op de KennisHub het Hupper-college "${thema}" tegen en dat lijkt me een mooie aanvulling op mijn werk. Zou ik dit college mogen volgen?`,
    '',
    `Meer over het college: ${url}`,
    '',
    'Alvast bedankt!',
    '',
    'Hartelijke groet,',
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function AanvraagDialog({
  college,
  onClose,
}: {
  college: HupperCollege;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="hupper-dialog-title"
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-ring2-bg text-ring2-fg">
              <MailIcon size={18} />
            </span>
            <h3 id="hupper-dialog-title" className="text-[17px] font-bold text-ink-900">
              College aanvragen
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-md p-1 text-ink-900/40 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>

        <p className="mt-4 text-[14px] leading-relaxed text-ink-900/80">
          Je vraagt het college <b className="font-semibold text-ink-900">{college.thema}</b> aan
          bij je teamleider. Er opent een e-mail in je mailprogramma met de aanvraag alvast
          ingevuld.
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-900/60">
          Vul zelf het e-mailadres van je teamleider in en verstuur het bericht. Je teamleider
          regelt de toegang en je ontvangt de link naar het college per mail.
        </p>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-brand-200 px-4 py-2 text-[13px] font-semibold text-ink-900/70 hover:bg-brand-50"
          >
            Annuleren
          </button>
          <a
            href={hupperMailto(college)}
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-600"
          >
            <SendIcon size={14} />
            E-mail openen
          </a>
        </div>
      </div>
    </div>
  );
}

export function HupperColleges(): JSX.Element {
  const [college, setCollege] = useState<HupperCollege | null>(null);

  return (
    <section>
      <h2 className="mb-1 flex items-center gap-2 text-[17px] font-bold text-ink-900">
        <GraduationCapIcon size={18} className="text-ring2-fg" />
        Erkende online colleges van Hupper
      </h2>
      <p className="mb-4 max-w-2xl text-[13px] leading-relaxed text-ink-900/60">
        Hupper, onze gecertificeerde kennispartner, biedt e-learnings voor professionals in de ggz.
        Je hoeft geen Hupper-coins aan te schaffen: vraag een college aan bij je teamleider en je
        ontvangt de link per mail. Klik op een thema voor de collegepagina op hupper.nl.
      </p>
      <div className="overflow-x-auto rounded-2xl border border-brand-100 bg-white">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-brand-50 text-[11px] uppercase tracking-wide text-ink-900/60">
            <tr>
              <th className="px-4 py-2.5 font-semibold">Thema</th>
              <th className="px-4 py-2.5 font-semibold">Over dit college</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-100">
            {HUPPER_COLLEGES.map((c) => (
              <tr key={c.url} className="hover:bg-brand-50/50">
                <td className="whitespace-nowrap px-4 py-3 align-top">
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-petrol-500 hover:underline"
                  >
                    {c.thema}
                    <ExternalLinkIcon size={12} strokeWidth={2} />
                  </a>
                </td>
                <td className="px-4 py-3 align-top leading-relaxed text-ink-900/65">
                  {c.beschrijving}
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <button
                    type="button"
                    onClick={() => setCollege(c)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-brand-500 bg-white px-2.5 py-1 text-[12px] font-semibold text-brand-700 hover:bg-brand-50"
                  >
                    <SendIcon size={13} />
                    Aanvragen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {college && <AanvraagDialog college={college} onClose={() => setCollege(null)} />}
    </section>
  );
}
